import apiRoutes from '@constants/apiRoutes';
import baseApi from '@services/api';
import { ApiResponse } from './types/common';
import {
  CompanyInvestorReportListingData,
  CompanyReportListingQueryParams,
  CompanyInvestorReportDetailDto,
  CompanySlidesResponseDto,
  SlideDetailsRequestParams,
  CompanyGames,
  ReqBatchInvestorReportDto,
  CompanyReportStatus,
  SlideSuggestionsPayload,
} from './types/investor-report-v2';
import {
  GameContactPoc,
  ReqUpdateSuggestionDto,
  ResInvestorReportSuggestionDto,
  SlideDetailsData,
  SuggestionSlidePayload,
  ReqInvestorReportSuggestionDto,
  SuggestionDto,
} from './types/investor-report';
import { SlideData } from '../pages/Dashboard/components/InvestorReport/InvestorReportEdit/components/Reel/components/Section/components/Slide/types';
import {
  InvestorReportShareItem,
  ShareReportPayload,
  RevokeReportSharePayload,
  SharedCompany,
} from './types/investor-report/investorReportShare';

const investorReportV2 = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getV2InvestorReports: builder.query<
      ApiResponse<CompanyInvestorReportListingData>,
      CompanyReportListingQueryParams
    >({
      query: (params: CompanyReportListingQueryParams) => ({
        url: apiRoutes.investorReportV2Listing,
        params: {
          pageNumber: params.pageNumber ?? 0,
          pageSize: params.pageSize ?? 50,
          ...(params.search ? { search: params.search } : {}),
          ...(params.statusFilter?.length ? { status: params.statusFilter } : {}),
        },
      }),
    }),

    getV2InvestorReportByClientCode: builder.query<
      ApiResponse<CompanyInvestorReportDetailDto>,
      string
    >({
      query: (clientCode: string) => ({
        url: apiRoutes.investorReportV2ByClientCode(clientCode),
      }),
    }),

    getV2InvestorReportSlidesByClientCode: builder.query<
      ApiResponse<CompanySlidesResponseDto>,
      string
    >({
      query: (clientCode: string) => ({
        url: apiRoutes.investorReportV2SlidesByClientCode(clientCode),
      }),
    }),

    getV2InvestorReportSlideById: builder.query<
      ApiResponse<SlideDetailsData>,
      SlideDetailsRequestParams
    >({
      query: ({ clientCode, slideId }) => ({
        url: apiRoutes.investorReportV2SlideById(clientCode, slideId),
      }),
    }),

    getV2InvestorReportCompanies: builder.query<ApiResponse<CompanyGames[]>, void>({
      query: () => ({
        url: apiRoutes.investorReportV2Companies,
      }),
    }),

    getV2AllCompanies: builder.query<ApiResponse<CompanyGames[]>, void>({
      query: () => ({
        url: apiRoutes.investorReportV2AllCompanies,
      }),
    }),

    generateV2InvestorReport: builder.mutation<ApiResponse<void>, ReqBatchInvestorReportDto>({
      query: (payload) => ({
        url: apiRoutes.investorReportV2Generate,
        method: 'POST',
        body: payload,
      }),
    }),

    getV2CompanyReportStatus: builder.query<ApiResponse<CompanyReportStatus>, string>({
      query: (clientCode: string) => ({
        url: apiRoutes.investorReportV2CompanyStatus(clientCode),
      }),
    }),

    // --- Company Refresh ---
    refreshV2CompanyReport: builder.mutation<
      ApiResponse<CompanyReportStatus>,
      { clientCode: string; cohortCutoff?: number }
    >({
      query: ({ clientCode, cohortCutoff }) => ({
        url: apiRoutes.investorReportV2CompanyRefresh(clientCode),
        method: 'POST',
        body: typeof cohortCutoff === 'number' ? { cohortCutoff } : undefined,
      }),
    }),

    // --- Company Delete ---
    deleteV2CompanyInvestorReport: builder.mutation<ApiResponse<null>, { clientCode: string }>({
      query: ({ clientCode }) => ({
        url: apiRoutes.investorReportV2ByClientCode(clientCode),
        method: 'DELETE',
      }),
    }),

    // --- AI Commentary: single company slide ---
    generateV2CompanySlideAiCommentary: builder.mutation<
      ApiResponse<void>,
      { clientCode: string; slideId: string }
    >({
      query: ({ clientCode, slideId }) => ({
        url: apiRoutes.investorReportV2CompanySlideAiCommentary(clientCode, slideId),
        method: 'POST',
      }),
    }),

    // --- Company Share ---
    shareV2CompanyReport: builder.mutation<
      ApiResponse<InvestorReportShareItem[]>,
      { clientCode: string; payload: ShareReportPayload }
    >({
      query: ({ clientCode, payload }) => ({
        url: apiRoutes.investorReportV2CompanyShare(clientCode),
        method: 'POST',
        body: payload,
      }),
    }),

    getV2CompanyShareList: builder.query<
      ApiResponse<InvestorReportShareItem[]>,
      { clientCode: string }
    >({
      query: ({ clientCode }) => apiRoutes.investorReportV2CompanyShare(clientCode),
    }),

    revokeV2CompanyShare: builder.mutation<
      ApiResponse<null>,
      { clientCode: string; payload: RevokeReportSharePayload }
    >({
      query: ({ clientCode, payload }) => ({
        url: apiRoutes.investorReportV2CompanyRevokeShare(clientCode, payload.type, payload.id),
        method: 'DELETE',
      }),
    }),

    getV2CompanyShareLink: builder.query<ApiResponse<string>, { clientCode: string }>({
      query: ({ clientCode }) =>
        apiRoutes.investorReportV2CompanyShareLink(clientCode, 'INVESTOR_REPORT_V2_EXT'),
    }),

    getV2CompanyContactDetails: builder.query<
      ApiResponse<GameContactPoc[]>,
      { clientCode: string }
    >({
      query: ({ clientCode }) => apiRoutes.investorReportV2CompanyContactDetails(clientCode),
    }),

    getV2SharedGames: builder.query<ApiResponse<SharedCompany[]>, void>({
      query: () => apiRoutes.investorReportV2SharedGames,
    }),

    // --- V2: Single-slide update ---
    updateV2SlideData: builder.mutation<
      void,
      { payload: SlideData; clientCode: string; slideId: string }
    >({
      query: ({ payload, clientCode, slideId }) => ({
        url: apiRoutes.investorReportV2UpdateSlide(clientCode, slideId),
        method: 'PUT',
        body: payload,
      }),
    }),

    // --- V2: Single-slide update (client-allowed) ---
    updateV2SlideDataClientAllowed: builder.mutation<
      void,
      { payload: SlideData; clientCode: string; slideId: string }
    >({
      query: ({ payload, clientCode, slideId }) => ({
        url: apiRoutes.investorReportV2UpdateSlideClientAllowed(clientCode, slideId),
        method: 'PUT',
        body: payload,
      }),
    }),

    // --- V2: Single-slide chart upload ---
    uploadV2ChartImage: builder.mutation<
      ApiResponse<string>,
      { clientCode: string; slideId: string; file: File }
    >({
      query: ({ clientCode, slideId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: apiRoutes.investorReportV2UploadChartImage(clientCode, slideId),
          method: 'POST',
          body: formData,
        };
      },
    }),

    // --- V2: Get chart image signed URL ---
    getV2ChartImageUrl: builder.query<
      ApiResponse<string>,
      { clientCode: string; slideId: string; fileName: string }
    >({
      query: ({ clientCode, slideId, fileName }) => ({
        url: apiRoutes.investorReportV2GetChartImageUrl(clientCode, slideId, fileName),
      }),
    }),

    // --- V2: AI company-info (company summary) per-slide ---
    generateV2CompanySlideCompanyInfo: builder.mutation<
      ApiResponse<void>,
      { clientCode: string; slideId: string }
    >({
      query: ({ clientCode, slideId }) => ({
        url: apiRoutes.investorReportV2CompanySlideCompanyInfo(clientCode, slideId),
        method: 'POST',
      }),
    }),

    // --- V2: Update skip-slide config ---
    updateV2SkipSlideConfig: builder.mutation<
      void,
      { clientCode: string; slideId: string; skipSlide: boolean }
    >({
      query: ({ clientCode, slideId, skipSlide }) => ({
        url: apiRoutes.investorReportV2UpdateSkipSlideConfig(clientCode, slideId),
        method: 'PUT',
        body: { skipSlide },
      }),
    }),

    updateV2SkipSlideAdminConfig: builder.mutation<
      void,
      { clientCode: string; slideId: string; skipSlide: boolean }
    >({
      query: ({ clientCode, slideId, skipSlide }) => ({
        url: apiRoutes.investorReportAdminV2UpdateSkipSlideConfig(clientCode, slideId),
        method: 'PUT',
        body: { skipSlide },
      }),
    }),

    getSuggestionsV2: builder.query<ApiResponse<SuggestionDto[]>, SlideSuggestionsPayload>({
      query: ({ clientCode, slideId }) =>
        apiRoutes.investorReportV2GetSuggestions(clientCode, slideId),
    }),

    reviewV2Suggestions: builder.mutation<
      ApiResponse<ResInvestorReportSuggestionDto[]>,
      SlideSuggestionsPayload & { payload: ReqUpdateSuggestionDto }
    >({
      query: ({ clientCode, slideId, payload }) => ({
        url: apiRoutes.investorReportV2SuggestionsReview(clientCode, slideId),
        method: 'POST',
        body: payload,
      }),
    }),

    createOrUpdateV2Suggestions: builder.mutation<
      ApiResponse<ResInvestorReportSuggestionDto[]>,
      SlideSuggestionsPayload & { suggestions: ReqInvestorReportSuggestionDto[] }
    >({
      query: ({ clientCode, slideId, suggestions }) => ({
        url: apiRoutes.investorReportV2Suggestions(clientCode, slideId),
        method: 'POST',
        body: suggestions,
      }),
    }),

    // --- V2: Batch generate ai commentary ---
    batchGenerateV2AiCommentary: builder.mutation<ApiResponse<void>, { clientCode: string }>({
      query: ({ clientCode }) => ({
        url: apiRoutes.investorReportV2BatchGenerateAiCommentary(clientCode),
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useLazyGetV2InvestorReportsQuery,
  useLazyGetV2InvestorReportByClientCodeQuery,
  useLazyGetV2InvestorReportSlidesByClientCodeQuery,
  useLazyGetV2InvestorReportSlideByIdQuery,
  useLazyGetV2InvestorReportCompaniesQuery,
  useLazyGetV2AllCompaniesQuery,
  useGenerateV2InvestorReportMutation,
  useLazyGetV2CompanyReportStatusQuery,
  useRefreshV2CompanyReportMutation,
  useDeleteV2CompanyInvestorReportMutation,
  useGenerateV2CompanySlideAiCommentaryMutation,
  useShareV2CompanyReportMutation,
  useLazyGetV2CompanyShareListQuery,
  useRevokeV2CompanyShareMutation,
  useLazyGetV2CompanyShareLinkQuery,
  useLazyGetV2CompanyContactDetailsQuery,
  useLazyGetV2SharedGamesQuery,
  useUpdateV2SlideDataMutation,
  useUpdateV2SlideDataClientAllowedMutation,
  useUploadV2ChartImageMutation,
  useLazyGetV2ChartImageUrlQuery,
  useGenerateV2CompanySlideCompanyInfoMutation,
  useUpdateV2SkipSlideConfigMutation,
  useBatchGenerateV2AiCommentaryMutation,
  useReviewV2SuggestionsMutation,
  useCreateOrUpdateV2SuggestionsMutation,
  useLazyGetSuggestionsV2Query,
  useUpdateV2SkipSlideAdminConfigMutation,
} = investorReportV2;
